import { SpinningWheel } from "@/components/SpinningWheel";
import { SectionLabel, PrimaryButton, SecondaryButton } from "@/components/Bits";
import { GraduationCap, Building2, TrendingUp, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const IMG_ATHLETE =
  "https://images.unsplash.com/photo-1710736460914-4a7f22d736c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxhdGhsZXRpYyUyMHRyYWluaW5nfGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc4MjAzNzQ2OXww&ixlib=rb-4.1.0&q=85";
const IMG_INFRA =
  "https://images.unsplash.com/photo-1651503773635-d989bab7b519?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzcG9ydHMlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzgyMDM3NDcwfDA&ixlib=rb-4.1.0&q=85";

const pillars = [
  {
    icon: GraduationCap,
    title: "Education",
    text: "Knowledge that raises standards, promotes literacy and contributes to a sustainable workforce across markets.",
  },
  {
    icon: Building2,
    title: "Infrastructure",
    text: "The facilities, systems and foundations that let sport and communities grow.",
  },
  {
    icon: TrendingUp,
    title: "Development",
    text: "Talent, pathways and people that turn potential into lasting performance.",
  },
];

export default function Home() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7 fade-up">
              <SectionLabel index="01">Find Your Zone</SectionLabel>
              <h1 className="mt-8 font-heading font-bold uppercase leading-[0.92] tracking-tighter text-[#111111] text-5xl md:text-7xl lg:text-8xl">
                Improve sport.
                <br />
                Improve the
                <br />
                world.
              </h1>
              <p className="mt-8 max-w-xl text-[#333333] text-base md:text-lg leading-relaxed">
                ZONA Sports operates at the core of the sporting industry. Through Education,
                Infrastructure, and Development, we transform ideas into an ongoing cycle of
                growth and innovation.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <PrimaryButton to="/contact" testid="hero-cta-primary">
                  Get in touch
                </PrimaryButton>
                <SecondaryButton to="/about" testid="hero-cta-secondary">
                  Our mission
                </SecondaryButton>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end fade-up">
              <SpinningWheel size={340} />
            </div>
          </div>
        </div>
      </section>

      {/* ETHOS STRIP */}
      <section className="border-b border-[#E5E5E5] bg-[#111111]">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-10">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-center">
            {["Education", "Infrastructure", "Development"].map((w, i) => (
              <div key={w} className="flex items-center gap-10">
                <span className="font-heading text-2xl md:text-4xl uppercase tracking-tight text-white">
                  {w}
                </span>
                {i < 2 && <span className="text-[#777777] text-2xl">•</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-3xl">
          <SectionLabel index="02">The Ethos</SectionLabel>
          <h2 className="mt-6 font-heading font-bold uppercase tracking-tight text-[#111111] text-3xl md:text-5xl leading-tight">
            A never-ending spinning wheel
          </h2>
          <p className="mt-6 text-[#333333] text-base md:text-lg leading-relaxed">
            Each part feeds the next. Education builds capability. Infrastructure gives it a home.
            Development carries it forward — and the momentum returns to the start.
          </p>
        </div>

        <div className="mt-14 grid gap-px bg-[#E5E5E5] md:grid-cols-3 border border-[#E5E5E5]">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-${p.title.toLowerCase()}`}
              className="group bg-white p-10 transition-colors hover:bg-[#111111]"
            >
              <div className="flex items-center justify-between">
                <p.icon size={32} className="text-[#111111] transition-colors group-hover:text-white" strokeWidth={1.5} />
                <span className="font-heading text-sm tracking-[0.3em] text-[#777777]">0{i + 1}</span>
              </div>
              <h3 className="mt-8 font-heading font-bold uppercase tracking-tight text-2xl text-[#111111] transition-colors group-hover:text-white">
                {p.title}
              </h3>
              <p className="mt-4 text-[#333333] leading-relaxed transition-colors group-hover:text-[#E5E5E5]">
                {p.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SPLIT FEATURE */}
      <section className="border-y border-[#E5E5E5]">
        <div className="mx-auto grid max-w-7xl items-stretch lg:grid-cols-2">
          <div className="px-6 md:px-10 py-16 md:py-24 lg:pr-16">
            <SectionLabel index="03">Why we exist</SectionLabel>
            <h2 className="mt-6 font-heading font-bold uppercase tracking-tight text-[#111111] text-3xl md:text-5xl leading-tight">
              Sport is the lever that moves people
            </h2>
            <p className="mt-6 text-[#333333] text-base md:text-lg leading-relaxed">
              We believe a stronger sporting world creates a stronger world. By raising standards in
              the industry, we create opportunity, health and community far beyond the pitch.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Promote a culture of evidence-led health and education",
                "Support independence by developing local infrastructure",
                "Sustainable economic impact in local communities",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#111111]">
                  <span className="mt-2 h-1.5 w-1.5 bg-[#111111]" />
                  <span className="font-heading text-sm uppercase tracking-[0.12em]">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link
                to="/services"
                data-testid="home-services-link"
                className="group inline-flex items-center gap-2 font-heading text-sm uppercase tracking-[0.16em] text-[#111111] border-b border-[#111111] pb-1"
              >
                Explore what we do
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          <div className="relative min-h-[340px] lg:min-h-full border-t border-[#E5E5E5] lg:border-t-0 lg:border-l">
            <video
              src="https://customer-assets.emergentagent.com/job_zona-sports-hub/artifacts/zm9r3xm6_rinchoa00000142.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={IMG_ATHLETE}
              data-testid="why-we-exist-video"
              className="absolute inset-0 h-full w-full object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#111111]">
        <img
          src={IMG_INFRA}
          alt="Stadium infrastructure"
          className="absolute inset-0 h-full w-full object-cover opacity-15 grayscale"
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 py-24 md:py-32 text-center">
          <h2 className="font-heading font-bold uppercase tracking-tight text-white text-4xl md:text-6xl leading-none">
            Let's build the wheel together
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-[#E5E5E5] text-base md:text-lg leading-relaxed">
            Partners, clubs, institutions, and innovators in sport, let's have a conversation!
          </p>
          <div className="mt-10 flex justify-center">
            <Link
              to="/contact"
              data-testid="home-bottom-cta"
              className="group inline-flex items-center gap-2 bg-white px-8 py-4 font-heading text-sm uppercase tracking-[0.18em] text-[#111111] transition-colors hover:bg-[#E5E5E5]"
            >
              Get in touch
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
